import { makeStyles } from '@material-ui/core';
import { Events } from "@type-r/models";
import { useEvent, useIO } from "@type-r/react";
import { RelationshipModel } from "api/relationship";
import { Column, Columns, Flex, FormCheckbox, FormInput, FormSelect, Loading, Option, ReadOnly, Toolbar } from 'lqx-react';
import React, { useEffect } from "react";
import { MultiEmailInput } from "../../../../../common/emails";
import { CpSaveButton } from "../common/api";

export const enum InvoiceSendingMethod {
    EDI = 'EDI',
    InblockEdi = 'InB EDI',
    Email = 'Email',
    HardCopy = 'Hard Copy',
    FTP = 'FTP',
    NotApplicable = 'N/A',
    SBI = 'SBI',
    Site = 'Site',
    WebPortal = 'Web Portal'
};

export const CpInvoicing = ({ $selected, canEdit }: {
    $selected: RelationshipModel,
    canEdit: Boolean
}) => {
    const classes = useStyles();

    const invoicing = $selected.cpInvoicing;
    const sendingResponsibility = ['Site', 'GBS'];
    const sendingMethods = ['EDI', 'InB EDI', 'Email', 'Hard Copy', 'FTP', 'N/A', 'SBI', 'Site', 'Web Portal'];
    const frequency = ['Daily', 'Bi-Weekly (even)', 'Bi-Weekly (Odd)', 'Weekly', 'Monthly'];
    const disableWebPortalControls = invoicing.$.sendingMethod.value !== 'Web Portal';
    const disableAddressControls = invoicing.$.sendingMethod.value !== 'Hard Copy';
    const disableSendInvoicesIndividually = invoicing.$.sendingMethod.value !== 'Email';
    const readOnly = React.useContext(ReadOnly) || !canEdit;

    const clearWebPortal = () => {
        invoicing.webPortalUrl = '';
        invoicing.webPortalUser = '';
        invoicing.webPortalPwd = '';
    }

    const clearHardCopy = () => {
        invoicing.streetAddress = '';
        invoicing.addressNumber = 0;
        invoicing.zipCode = '';
        invoicing.city = '';
        invoicing.region = '';
        invoicing.country = '';
    }

    const loadData = async () => {
        await $selected.cpInvoicing.fetch().catch(onError);
    }
    const isReady = useIO(loadData, []);

    useEvent(Events, "cpImport:done", loadData);

    const save = async () => {
        await $selected.cpInvoicing.save();
    }

    const onError = (e) => {
        console.error(e);
    }

    useEffect(() => {
        if (disableWebPortalControls) {
            clearWebPortal();
        }
        if (disableAddressControls) {
            clearHardCopy();
        }
    }, [invoicing.sendingMethod])

    return (
        isReady ? <>
            <ReadOnly.Provider value={readOnly}>
                <Columns className={classes.container}>
                    <Column>
                        <FormInput label="Customer ID" $value={invoicing.$.customerId} />
                        <FormSelect label="Sending Method" $value={invoicing.$.sendingMethod} >
                            {sendingMethods.map(sm => <Option key={sm} value={sm}>{sm}</Option>)}
                        </FormSelect>
                        <FormSelect label="Sending Responsibility" $value={invoicing.$.sendingResponsibility} >
                            {sendingResponsibility.map(sr => <Option key={sr} value={sr}>{sr}</Option>)}
                        </FormSelect>
                        <FormInput label="Invoice Submission Owner" $value={invoicing.$.invoiceSubmissionOwner} />
                        <MultiEmailInput $emails={invoicing.$.emails as any} label={'Receipt Mail Address'} />
                        <FormInput label="Web Portal URL" $value={invoicing.$.webPortalUrl} disabled={disableWebPortalControls} />
                        <FormInput label="Web Portal User" $value={invoicing.$.webPortalUser} disabled={disableWebPortalControls} />
                        <FormInput type="password" label="Web Portal Pwd" $value={invoicing.$.webPortalPwd} disabled={disableWebPortalControls} />
                    </Column>
                    <Column>
                        <FormSelect label="Frequency" $value={invoicing.$.frequency} >
                            {frequency.map(f => <Option key={f} value={f}>{f}</Option>)}
                        </FormSelect>
                        <FormCheckbox label="Auto Send Invoice Enabled" $checked={invoicing.$.autoSendInvoices} readOnly={!canEdit} />
                        <FormCheckbox label="Send Invoice Individually" $checked={invoicing.$.sendInvoicesIndividually} readOnly={disableSendInvoicesIndividually} />
                        <FormInput label="Street Address" $value={invoicing.$.streetAddress} disabled={disableAddressControls} />
                        <FormInput label="Address Number" positive number $value={invoicing.$.addressNumber} disabled={disableAddressControls} />
                        <FormInput label="ZIP Code" $value={invoicing.$.zipCode} disabled={disableAddressControls} />
                        <FormInput label="City" $value={invoicing.$.city} disabled={disableAddressControls} />
                        <FormInput label="State / Region" $value={invoicing.$.region} disabled={disableAddressControls} />
                        <FormInput label="Country" $value={invoicing.$.country} disabled={disableAddressControls} />
                        <Flex grow />
                    </Column>
                </Columns>
            </ReadOnly.Provider>
            <Toolbar>
                <Flex grow />
                {canEdit && <CpSaveButton onClick={save} label={'Save invoicing data?'} />}
            </Toolbar>
        </> : < Loading />
    );
}

const useStyles = makeStyles({
    container: {
        alignItems: "baseline"
    }
});